import {useEffect, useRef} from 'react';

import {trackWorkspaceRunSelectorInventory} from '../../../services/analytics/workspaceRunSelectorInventoryEvents';
import {useReduxStore} from '../../../state/hooks';
import {
  lookupPart,
  type StateType as PartsState,
} from '../../../state/views/normalizerSupport';
import type {Ref as RunSetRef} from '../../../state/views/runSet/types';
import {
  type FilterV2Item,
  type FilterV2Root,
  isEmptyPlaceholderItem,
  isFilterV2Group,
} from '../../../util/filterV2';
import {DEFAULT_RUNS_SORT} from '../../../util/queryts';
import type {Sort} from '../../../util/queryTypes';
import type {Config as RunFeedConfig} from '../../../util/runfeed';
import * as Run from '../../../util/runs';
import {
  GROUP_BY_ALL_KEY,
  type RunKeySection,
  runKeySections,
} from '../../../util/runTypes';
import * as SM from '../../../util/selectionmanager';
import {getNamedWorkspaceType} from '../../Views/utils';
import {useMaybeWorkspaceRefsContext} from '../../Workspace/WorkspaceRefsContext';

export const RUN_SELECTOR_INVENTORY_SCHEMA_VERSION = 1;

type FilterInventory = Pick<
  RunSelectorInventory,
  | 'numActiveFilters'
  | 'numInactiveFilters'
  | 'filterKeySections'
  | 'numActiveFilterGroups'
  | 'numInactiveFilterGroups'
>;

export type RunSelectorInventory = {
  numActiveFilters: number;
  numInactiveFilters: number;
  filterKeySections: RunKeySection[];
  numActiveFilterGroups: number;
  numInactiveFilterGroups: number;
  numGroupingKeys: number;
  groupKeySections: RunKeySection[];
  hasCustomSort: boolean;
  numSortKeys: number;
  sortKeySections: RunKeySection[];
  hasRunVisibilityOverride: boolean;
  numPinnedRuns: number;
  hasBaselineRun: boolean;
  numPinnedColumns: number;
  pinnedColumnKeySections: RunKeySection[];
};

const RUN_KEY_SECTION_SET = new Set<string>(runKeySections);

function sortedSections(sections: Iterable<string>): RunKeySection[] {
  return [...new Set(sections)]
    .filter((section): section is RunKeySection =>
      RUN_KEY_SECTION_SET.has(section)
    )
    .sort();
}

export function summarizeFilters(filters: FilterV2Root): FilterInventory {
  const sections = new Set<string>();
  let numActiveFilters = 0;
  let numInactiveFilters = 0;
  let numActiveFilterGroups = 0;
  let numInactiveFilterGroups = 0;

  const visit = (items: FilterV2Item[], ancestorsActive: boolean) => {
    for (const item of items) {
      if (isFilterV2Group(item)) {
        const active = ancestorsActive && !item.disabled;
        if (active) {
          numActiveFilterGroups++;
        } else {
          numInactiveFilterGroups++;
        }
        visit(item.filters, active);
      } else if (!isEmptyPlaceholderItem(item)) {
        sections.add(item.key.section);
        if (ancestorsActive && !item.disabled) {
          numActiveFilters++;
        } else {
          numInactiveFilters++;
        }
      }
    }
  };

  visit(filters.filters, true);
  return {
    numActiveFilters,
    numInactiveFilters,
    filterKeySections: sortedSections(sections),
    numActiveFilterGroups,
    numInactiveFilterGroups,
  };
}

export function hasRunVisibilityOverride(
  groupSelections: SM.GroupSelectionState
): boolean {
  return (
    groupSelections.selections.root !== SM.Root.All ||
    groupSelections.selections.tree.length > 0
  );
}

function summarizeGrouping(grouping: SM.GroupSelectionState['grouping']) {
  const groups = grouping.filter(key => !Run.keysEqual(key, GROUP_BY_ALL_KEY));
  return {
    numGroupingKeys: groups.length,
    groupKeySections: sortedSections(groups.map(key => key.section)),
  };
}

function summarizeSort(sort: Sort) {
  const hasCustomSort =
    sort.keys.length !== DEFAULT_RUNS_SORT.keys.length ||
    sort.keys.some((sortKey, index) => {
      const defaultKey = DEFAULT_RUNS_SORT.keys[index];
      return (
        defaultKey == null ||
        sortKey.ascending !== defaultKey.ascending ||
        !Run.keysEqual(sortKey.key, defaultKey.key)
      );
    });
  return {
    hasCustomSort,
    numSortKeys: sort.keys.length,
    sortKeySections: sortedSections(sort.keys.map(key => key.key.section)),
  };
}

function summarizePinnedColumns(runFeed: RunFeedConfig) {
  const pinnedColumns = Object.entries(runFeed.columnPinned)
    .filter(([, pinned]) => pinned)
    .map(([accessor]) => Run.keyFromString(accessor))
    .filter((key): key is NonNullable<typeof key> => key != null);
  return {
    numPinnedColumns: pinnedColumns.length,
    pinnedColumnKeySections: sortedSections(
      pinnedColumns.map(key => key.section)
    ),
  };
}

export function summarizeRunSelectorInventory(
  parts: PartsState,
  runSetRef: RunSetRef
): RunSelectorInventory {
  const runSet = lookupPart(parts, runSetRef);
  const filters = lookupPart(parts, runSet.filtersRef) as FilterV2Root;
  const sort = lookupPart(parts, runSet.sortRef);
  const groupSelections = lookupPart(parts, runSet.groupSelectionsRef);

  return {
    ...summarizeFilters(filters),
    ...summarizeGrouping(groupSelections.grouping),
    ...summarizeSort(sort),
    hasRunVisibilityOverride: hasRunVisibilityOverride(groupSelections),
    numPinnedRuns: runSet.pinnedRunIds?.length ?? 0,
    hasBaselineRun: runSet.baselineRunId != null,
    ...summarizePinnedColumns(runSet.runFeed),
  };
}

function getWorkspaceTypeSafely(
  view: Parameters<typeof getNamedWorkspaceType>[0] | undefined
) {
  if (view?.user == null) {
    return 'unknown' as const;
  }
  try {
    return getNamedWorkspaceType(view);
  } catch {
    return 'unknown' as const;
  }
}

export function useTrackRunSelectorInventory({
  runSetRef,
}: {
  runSetRef: RunSetRef;
}) {
  const store = useReduxStore();
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const lastTrackedWorkspaceRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const primaryRunSetRef = workspaceRefs?.runSetRefs[0];
    const workspaceKey =
      workspaceRefs == null
        ? undefined
        : `${workspaceRefs.viewRef.viewID}:${runSetRef.id}`;
    if (
      workspaceRefs == null ||
      workspaceKey === lastTrackedWorkspaceRef.current ||
      primaryRunSetRef?.id !== runSetRef.id ||
      primaryRunSetRef.viewID !== runSetRef.viewID
    ) {
      return;
    }

    const state = store.getState().views;
    const view = state.views[workspaceRefs.viewRef.viewID];
    if (view == null) {
      return;
    }

    trackWorkspaceRunSelectorInventory({
      schemaVersion: RUN_SELECTOR_INVENTORY_SCHEMA_VERSION,
      entityName: view.project?.entityName ?? view.entityName ?? '',
      projectName: view.project?.name ?? '',
      viewId: view.id,
      runSetId: runSetRef.id,
      viewName: view.name,
      viewType: workspaceRefs.viewRef.type,
      workspaceType: getWorkspaceTypeSafely(view),
      ...summarizeRunSelectorInventory(state.parts, runSetRef),
    });
    lastTrackedWorkspaceRef.current = workspaceKey;
  }, [runSetRef, store, workspaceRefs]);
}
